import { Inject, Injectable } from '@angular/core';
import LZString from 'lz-string';
import _, { ListIterator } from 'underscore';
import { IColumnMetaData, IReportLayoutSetting, IReportLayoutSettingColumn, IReportMetadata, ReportDataService } from '@app/shared/report/report-data.service';
import { AuthService } from '@app/shared/services/auth/auth.service';
import { IReportTableSettingsModel } from '@app/main/report/report-table-settings.component';
import { IStoredReportColumn } from '@app/shared/report/stored-report-data.service';
import { IExtendedWindow, IObject } from '@type_definitions/datatypes';
import { GaWebStorageService, STORAGE_REPORT_LAYOUT_SETTINGS, STORAGE_REPORT_LS_END_TIME, STORAGE_REPORT_LS_START_TIME } from '@app/shared/services/storage/ga-web-storage.service';
import { util } from '@app/shared/util';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { ReportColumnSizingType, ReportDisplayType } from '@app/shared/report/report-grid.service';
import moment, { Moment } from 'moment';

export interface IReportLayoutPrimaryKey {
  gridCode:string;
  ownerId:number;
  menuId?:number;
  storedReportId?:string;
}

export const LAYOUT_SETTINGS_SEND_INTERVAL:number = 30 * 1000; // the time interval, measured in milliseconds, for sending the layout settings to the server.
const MAX_WAITING_START_SEND_TIME:number = 4 * 60; // Maximum wait time (in seconds) before initiating the sending of report layout settings.
const MAX_WAITING_END_SEND_TIME:number = 30; // Maximum grace period (in seconds) to wait after successfully saving report layout settings.

@Injectable()
export class ReportLayoutSettingsService {
  private _layoutSettings:IReportLayoutSetting[] = [];
  private _backupLayoutSettings:IReportLayoutSetting[] = [];
  private _onStorageChangeSubject:BehaviorSubject<IReportLayoutSetting[]>;
  private _onSavedSubject:Subject<void> = new Subject();
  private intervalId:number;
  private onStorageChange:Observable<IReportLayoutSetting[]>;

  public onSaved:Observable<void> = this._onSavedSubject.asObservable();

  constructor(
    private reportDataService:ReportDataService,
    private authService:AuthService,
    private webStorageService:GaWebStorageService,
    @Inject(Window) private window:IExtendedWindow
  ) {
    const storageLayoutSettings:IReportLayoutSetting[] = this.getStorageLayoutSettings();
    this.layoutSettings = storageLayoutSettings;

    // use 'BehaviorSubject' so it's triggered the first time this service initiated with the value coming from localStorage
    this._onStorageChangeSubject = new BehaviorSubject(storageLayoutSettings);
    this.onStorageChange = this._onStorageChangeSubject.asObservable();
  }

  /**
   * Retrieves the backup layout settings for the report.
   *
   * @return {IReportLayoutSetting[]} The array of layout settings that are used as a backup.
   */
  public get backupLayoutSettings():IReportLayoutSetting[] {
    return this._backupLayoutSettings;
  }

  /**
   * Backs up the report layout settings.
   *
   * @param {IReportLayoutSetting[]} layoutSettings - An array of layout setting objects representing the report layout configurations to be backed up.
   */
  public set backupLayoutSettings(layoutSettings:IReportLayoutSetting[]) {
    this._backupLayoutSettings = layoutSettings;
  }

  /**
   * Retrieves the current layout settings.
   *
   * @return {IReportLayoutSetting[]} An array of layout settings.
   */
  public get layoutSettings():IReportLayoutSetting[] {
    return this._layoutSettings;
  }

  /**
   * Sets the configuration for layout settings.
   *
   * @param {IReportLayoutSetting[]} layoutSettings - An array of ILayoutSetting objects that define the layout settings configuration.
   */
  public set layoutSettings(layoutSettings:IReportLayoutSetting[]) {
    this._layoutSettings = layoutSettings;
  }

  /**
   * Retrieves a predefined layout setting based on the specified report ID, menu ID, and grid code.
   *
   * @param {number} reportId - The unique identifier for the report.
   * @param {number} menuId - The unique identifier for the menu.
   * @param {string} gridCode - The specific code related to the layout setting.
   * @param {string} storedReportId - Stored report id or code.
   * @return {IReportLayoutSetting} The predefined layout setting object.
   */
  public getPredefinedLayoutSetting(reportId:number, menuId:number, gridCode:string, storedReportId:string):IReportLayoutSetting {
    return {
      reportId: Number(reportId),
      menuId,
      gridCode,
      storedReportId,
      ownerId: this.authService.getCurrentUser().id,
      isChanged: false
    };
  }

  /**
   * Finds a layout setting matching the provided key within the given settings array.
   *
   * @param {IReportLayoutPrimaryKey} key - The key to match within the layout settings.
   * @param {IReportLayoutSetting[]} [settings=this.layoutSettings] - An optional array of layout settings to search within. Defaults to this.layoutSettings.
   * @return {IReportLayoutSetting} The first matching layout setting that matches the provided key, or undefined if no match is found.
   */
  public findLayoutSetting(key:IReportLayoutPrimaryKey, settings:IReportLayoutSetting[] = this.layoutSettings):IReportLayoutSetting {
    return _.find(settings, this.getLayoutSettingPredicates(key));
  }

  /**
   * Loads the report layout settings based on the provided grid codes and optional menuId.
   *
   * @param {string[]} gridCodes - An array of report layout setting grid codes.
   * @param {number} [menuId] - An optional menuId to filter the report layout settings.
   * @param {string} [storedReportId] - An optional storedReportId to filter the report layout settings.
   * @return {Promise<IReportLayoutSetting[]>} A promise that resolves to an array of report layout settings.
   */
  public async loadReportLayoutSettings(gridCodes:string[], menuId?:number, storedReportId?:string):Promise<IReportLayoutSetting[]> {
    if (!gridCodes) {
      return Promise.resolve([]);
    }

    // Synchronize in-memory layout settings with localStorage
    this.layoutSettings = this.getStorageLayoutSettings();

    const localLayoutSettings:IReportLayoutSetting[] = util.deepClone(this.layoutSettings);
    /**
     * Verifies all `gridCodes` exist in `localLayoutSettings` and optionally match `menuId`.
     * @type {boolean}
     */
    const isLocalSearch:boolean = _.every(gridCodes, (gridCode:string):boolean =>
      _.some(localLayoutSettings, this.getLayoutSettingPredicates({ gridCode, menuId, storedReportId, ownerId: this.authService.getCurrentUser()?.id }))
    );

    // if local layout settings are not empty then return this one and don't fetch from server
    if (isLocalSearch) {
      // Update the backup layout setting to be used for comparison when saving layout settings
      this.backupLayoutSettings = util.deepClone(this.layoutSettings);

      return Promise.resolve(this.layoutSettings);
    }

    return this.reportDataService.getReportLayoutSettings(gridCodes, menuId, storedReportId)
      .then((layoutSettings:IReportLayoutSetting[]) => {
        let processedLayoutSettings:IReportLayoutSetting[];

        // Use the memory layout settings if the server response is empty and local layout settings are not empty
        if (_.isEmpty(layoutSettings) && !_.isEmpty(this.layoutSettings)) {
          processedLayoutSettings = localLayoutSettings;
        } else {
          /*
           * Merges layoutSettings (from server) with localLayoutSettings:
           * - Iterates over layoutSettings, looking for matches in localLayoutSettings
           *   based on `gridCode` and `menuId`.
           * - Replaces matching layoutSettings with ones from localLayoutSettings,
           *   removing them from 'memoryLayoutSettings'.
           * - Concatenates any remaining unprocessed localLayoutSettings to the result,
           *   ensuring all settings are included.
           */
          processedLayoutSettings = _.chain(layoutSettings)
            .map((layoutSetting:IReportLayoutSetting) => {
              // mark layout setting as unchanged
              layoutSetting.isChanged = false;

              // Find and remove memory layout setting if it exists
              const index:number = _.findIndex(localLayoutSettings, this.getLayoutSettingPredicates(layoutSetting, this.authService.getCurrentUser()?.id));

              if (index !== -1) {
                const localLayoutSetting:IReportLayoutSetting = localLayoutSettings.splice(index, 1)[0];
                return localLayoutSetting || layoutSetting;
              }

              return layoutSetting;
            })
            .concat(localLayoutSettings)
            .value();
        }

        // Update the backup layout setting to be used for comparison when saving layout settings
        this.backupLayoutSettings = util.deepClone(processedLayoutSettings);

        // update layout setting records
        this.layoutSettings = processedLayoutSettings;

        // update local storage
        this.saveLayoutSettingsToStorage();

        return processedLayoutSettings;
      });
  }

  /**
   * Encodes the provided layout setting into a compressed, encoded URI component string.
   *
   * @param {IReportLayoutSetting} layoutSetting - The layout setting object to be encoded. Must not be null or undefined.
   * @param {boolean} [toBinaryData] - Optional flag to indicate if the data should be encoded to binary format. Currently unused.
   * @return {string} The compressed, encoded string representation of the layout setting, or undefined if the input is null or undefined.
   */
  public encodeLayoutSetting(layoutSetting:IReportLayoutSetting, toBinaryData?:boolean):string {
    if (!layoutSetting) {
      return undefined;
    }

    const copyLayoutSetting:IReportLayoutSetting = util.deepClone(layoutSetting);
    const encodingMethod:(input:string) => string = toBinaryData ? LZString.compressToUTF16 : LZString.compressToEncodedURIComponent;

    if (!toBinaryData) {
      // remove display type, and isChanged
      // because we don't need these objects to be stored in URL
      delete copyLayoutSetting.displayType;
      delete copyLayoutSetting.isChanged;
    }

    return encodingMethod(JSON.stringify(copyLayoutSetting));
  }

  /**
   * Decodes the given layout setting token into an ILayoutSetting object.
   *
   * @param {string} layoutSettingToken - The token representing the layout settings, which is expected to be a compressed and encoded URI component.
   * @param {boolean} [isBinaryData] - Indicates whether the token is binary data.
   * @return {IReportLayoutSetting | IReportLayoutSetting[] | undefined} - The decoded layout settings object if the token is valid; otherwise, returns undefined and falls back to default settings.
   */
  public decodeLayoutSetting<T = IReportLayoutSetting>(layoutSettingToken:string, isBinaryData?:boolean):T {
    if (!layoutSettingToken) {
      return;
    }

    const decodingMethod:(compressed:string) => string = isBinaryData ? LZString.decompressFromUTF16 : LZString.decompressFromEncodedURIComponent;

    try {
      return JSON.parse(decodingMethod(layoutSettingToken));
    } catch (e) {
      console.warn('Failed to decode layout setting token; using default settings', e);
      return undefined; // Fallback to default settings
    }
  }

  /**
   * Updates the metadata of columns based on the given layout settings.
   *
   * @param {IReportMetadata} reportMetadata - The metadata of the report containing columns to be updated.
   * @param {IReportLayoutSetting} layoutSetting - The layout settings containing column configurations.
   * @return {void}
   */
  public updateColumnMetadata(reportMetadata:IReportMetadata, layoutSetting:IReportLayoutSetting):void {
    _.each(reportMetadata.columns, (columnMetadata:IColumnMetaData) => {
      const predicate:IObject = { name: columnMetadata.name };
      const layoutSettingColumn:IReportLayoutSettingColumn = _.findWhere(layoutSetting.columns, predicate);
      const columnIndex:number = _.findIndex(layoutSetting.columns, predicate);

      columnMetadata.formatting.defaultinlist = !!layoutSettingColumn;

      if (layoutSettingColumn) {
        columnMetadata.listSequence = layoutSettingColumn.listSequence || (columnIndex * 10);
        columnMetadata.groupingSequence = layoutSettingColumn.groupingSequence;
        columnMetadata.aggregateOn = layoutSettingColumn.aggregateOn;
        columnMetadata.pivotSequence = layoutSettingColumn.pivotSequence;
      }
    });
  }

  /**
   * Converts report metadata and current layout settings into a new layout setting.
   *
   * @param {IReportMetadata} metadata - The metadata of the report to parse.
   * @param {IReportLayoutSetting} currentLayoutSetting - The current layout settings of the report.
   * @param {IReportTableSettingsModel} [tableSettings] - Optional table settings model to apply additional settings.
   *
   * @return {IReportLayoutSetting} The new report layout settings based on the provided metadata and current settings.
   */
  public parseMetadataToLayoutSetting(metadata:IReportMetadata, currentLayoutSetting:IReportLayoutSetting, tableSettings?:IReportTableSettingsModel):IReportLayoutSetting {
    if (util.isNullOrUndefinedOrEmpty(metadata) || util.isNullOrUndefinedOrEmpty(currentLayoutSetting)) {
      return undefined;
    }

    return util.deepClone<IReportLayoutSetting>({
      id: currentLayoutSetting.id,
      reportId: metadata.id,
      ownerId: currentLayoutSetting.ownerId,
      menuId: currentLayoutSetting.menuId,
      gridCode: currentLayoutSetting.gridCode,
      storedReportId: currentLayoutSetting.storedReportId,
      isChanged: currentLayoutSetting.isChanged,
      displayType: tableSettings?.display || ReportDisplayType.NORMAL,
      columnSizing: tableSettings?.columnSizing?.id || ReportColumnSizingType.HEADER,
      showSum: tableSettings?.sum || false,
      showCount: tableSettings?.count || false,
      showAverage: tableSettings?.average || false,
      showMinimum: tableSettings?.min || false,
      showMaximum: tableSettings?.max || false,
      columns: _.chain(metadata.columns)
        .filter((colMetadata:IColumnMetaData) => colMetadata.formatting.defaultinlist || !_.isUndefined(colMetadata.groupingSequence))
        .sortBy('listSequence')
        .map((colMetadata:IColumnMetaData):IReportLayoutSettingColumn => {
          const id:number = _.findWhere(currentLayoutSetting.columns, { name: colMetadata.name })?.id;

          return {
            id,
            columnNumber: Number(colMetadata.id),
            pivotSequence: colMetadata.pivotSequence,
            groupingSequence: colMetadata.groupingSequence,
            aggregateOn: colMetadata.aggregateOn,
            name: colMetadata.name
          };
        })
        .value()
    });
  }

  /**
   * Adds a new layout setting or updates an existing one based on the provided layout setting.
   *
   * @param {IReportLayoutSetting} layoutSetting - The layout setting object that needs to be added or updated.
   * @param {boolean} [updateStorage] - Optional flag indicating whether to update storage after adding or updating the layout setting.
   * @return {void} - No return value.
   */
  public addOrUpdateLayoutSetting(layoutSetting:IReportLayoutSetting, updateStorage?:boolean):void {
    if (!layoutSetting) {
      return;
    }

    const savedIndex:number = _.findIndex(this.layoutSettings, this.getLayoutSettingPredicates(layoutSetting));

    if (savedIndex > -1) {
      this.layoutSettings[savedIndex] = layoutSetting;
    } else {
      this.layoutSettings = this.layoutSettings.concat([layoutSetting]);
    }

    if (updateStorage) {
      this.saveLayoutSettingsToStorage();
    }
  }

  /**
   * Updates the backup layout setting with the provided layout setting object.
   * If an entry with matching gridCode, menuId, and ownerId exists in the backup settings, it will be replaced.
   *
   * @param {IReportLayoutSetting} layoutSetting - The layout setting object to be updated in the backup settings.
   * @return {void} This method does not return a value.
   */
  public updateBackupLayoutSetting(layoutSetting:IReportLayoutSetting):void {
    if (!layoutSetting) {
      return;
    }

    const savedIndex:number = _.findIndex(this.backupLayoutSettings, this.getLayoutSettingPredicates(layoutSetting));

    if (savedIndex > -1) {
      this._backupLayoutSettings[savedIndex] = layoutSetting;
    }
  }

  /**
   * Compares the layout settings of two report configurations to determine if they differ.
   *
   * This method checks each setting in the target layout setting against the corresponding setting in the source layout setting.
   * Special handling is applied to the 'columns' setting, where the method compares the arrays of columns to determine differences.
   *
   * @param {IReportLayoutSetting} targetLayoutSetting - The layout setting to compare against the source layout setting.
   * @param {IReportLayoutSetting} sourceLayoutSetting - The layout setting that serves as the baseline for comparison.
   * @return {boolean} - Returns true if there are differences between the target and source layout settings; otherwise, false.
   */
  public diffCompareLayoutSettings(targetLayoutSetting:IReportLayoutSetting, sourceLayoutSetting:IReportLayoutSetting):boolean {
    if (!sourceLayoutSetting) {
      return true;
    }

    const preprocessColumns = (columns: IReportLayoutSettingColumn[] = []): IReportLayoutSettingColumn[] => {
      return _.chain(columns)
        .map((column, index) => ({
          ...column,
          listSequence: util.isNullOrUndefinedOrEmpty(column.listSequence) ? index * 10 : column.listSequence
        }))
        .sortBy('listSequence') // Sort by unique properties to ensure consistency.
        .value();
    };

    const normalizedTarget:IReportLayoutSetting = {
      ...targetLayoutSetting,
      columns: preprocessColumns(targetLayoutSetting.columns)
    };

    const normalizedSource:IReportLayoutSetting = {
      ...sourceLayoutSetting,
      columns: preprocessColumns(sourceLayoutSetting.columns)
    };

    return !_.isEqual(normalizedTarget, normalizedSource);
  }

  /**
   * Compares the `columns` property of two metadata objects to determine if they are different.
   *
   * @param {IReportMetadata} currentReportMetadata - The current report metadata object.
   * @param {IReportMetadata} originalReportMetadata - The original report metadata object to compare against.
   * @return {boolean} Returns `true` if the `columns` properties are different, otherwise `false`.
   */
  public diffCompareMetadataColumns(currentReportMetadata:IReportMetadata, originalReportMetadata:IReportMetadata):boolean {
    const sourceColumns:IColumnMetaData[] = this.preProcessMetadataColumns(currentReportMetadata.columns);
    const targetColumns:IColumnMetaData[] = this.preProcessMetadataColumns(originalReportMetadata.columns);

    return !_.isEqual(sourceColumns, targetColumns);
  }

  public preProcessMetadataColumns(columns:IColumnMetaData[]):IColumnMetaData[] {
    return _.chain(util.deepClone(columns) || [])
      .sortBy('listSequence')
      .map((column:IColumnMetaData) => {
        return _.pick(
          column,
          // we only pick the interested keys of IColumnMetaData to be used for comparison
          ['id', 'name', 'formatting', 'aggregate', 'aggregateOn', 'groupingSequence', 'pivotSequence']
        );
      })
      .value();
  }

  /**
   * Saves the layout settings for the current user to local storage.
   *
   * This method iterates through the list of layout settings, filters them by
   * the current user's ownership, and updates the sequence order of columns for
   * proper storage. The modified settings are then stored locally using the
   * web storage service. Lastly, a notification is triggered to inform about
   * the storage change.
   *
   * @return {void} This function does not return any value.
   */
  private saveLayoutSettingsToStorage():void {
    const layoutSettings:IReportLayoutSetting[] = [];

    _.each(this.layoutSettings, (layoutSetting:IReportLayoutSetting) => {
      if (layoutSetting.ownerId === this.authService.getCurrentUser()?.id) {
        layoutSetting.columns.forEach((column:IStoredReportColumn, index:number) => {
          column.listSequence = index * 10;
        });

        layoutSettings.push(layoutSetting);
      }
    });

    this.setStorageLayoutSettings(layoutSettings);
  }

  /**
   * Saves layout settings to the server if there are changes detected.
   *
   * This method collects layout settings marked as changed from local storage,
   * removes the `isChanged` property from each, and sends them to the server
   * for saving or updating. Upon successful server response, it updates the
   * in-memory layout settings, backup layout settings, and persists the updates.
   *
   * The function does nothing if no layout settings have been marked as changed.
   */
  private saveLayoutSettingsToServer = ():void => {
    const layoutSettingsPayload:IReportLayoutSetting[] = _.map(this.getChangedStorageLayoutSettings(), (layoutSetting:IReportLayoutSetting) => {
      const updatedLayout:IReportLayoutSetting = { ...layoutSetting };
      delete updatedLayout.isChanged;
      return updatedLayout;
    });

    // check if there are any payloads and check the sending timestamp if conditions are met
    // when the sending timestamp conditions are not met then it tries again with the interval
    if (layoutSettingsPayload?.length && this.checkSendTimestamp()) {
      this.updateSendTimestamp(true);

      this.reportDataService.saveLayoutSettings(layoutSettingsPayload)
        .then((layoutSettings:IReportLayoutSetting[]) => {
          // update memory layout settings
          _.each(layoutSettings, (layoutSetting:IReportLayoutSetting) => {
            layoutSetting.isChanged = false;

            // update backup layout settings
            this.updateBackupLayoutSetting(layoutSetting);

            // update layout settings
            this.addOrUpdateLayoutSetting(layoutSetting, true);
          });

          // should clear interval because changes are saved
          this.window.clearInterval(this.intervalId);

          // notify 'onSaved'
          this._onSavedSubject.next();
        })
        .catch(util.noop)
        .finally(() => {
          // Update the send timestamp to indicate that the request has completed.
          this.updateSendTimestamp(false);
        });
    }
  };

  /**
   * Updates the layout settings for a specific report based on the given parameters.
   * If the selected layout setting is undefined or invalid, it assigns a predefined layout setting.
   * Processes and parses the metadata to generate a new layout setting and determines if it should
   * be updated in local storage, based on ownership and differences compared to a backup layout.
   *
   * @param {number} reportId - The ID of the report for which the layout settings are being updated.
   * @param {string} gridCode - The code representing the grid associated with the layout setting.
   * @param {IReportLayoutSetting} selectedLayoutSetting - The currently selected layout setting to be updated.
   * @param {IReportMetadata} currentReportMetadata - Metadata used to parse and construct the new layout setting.
   * @param {IReportMetadata} originalReportMetadata - Original report metadata used to compare with current metadata, original report metadata always up-to-date with latest personal layout settings
   * @param {IReportMetadata} originalStoredReportMetadata - Original stored report metadata used to compare with current report metadata.
   * @param {number} menuId - The menu ID linked to the layout settings operation.
   * @param {string} storedReportId - Stored report id linked to the layout settings operation.
   * @param {IReportTableSettingsModel} [tableSettingsModel] - Optional table settings model
   * @returns {IReportLayoutSetting} The updated layout setting.
   */
  public updateLayoutSettingsOnChange(
    reportId:number,
    gridCode:string,
    selectedLayoutSetting:IReportLayoutSetting,
    currentReportMetadata:IReportMetadata,
    originalReportMetadata?:IReportMetadata,
    originalStoredReportMetadata?:IReportMetadata,
    menuId?:number,
    storedReportId?:string,
    tableSettingsModel?:IReportTableSettingsModel
  ):IReportLayoutSetting {
    // if selected layout setting somehow is undefined, we set with predefined layout setting
    if (!selectedLayoutSetting) {
      selectedLayoutSetting = this.getPredefinedLayoutSetting(reportId, menuId, gridCode, storedReportId);
    }

    const layoutSetting:IReportLayoutSetting = this.parseMetadataToLayoutSetting(currentReportMetadata, selectedLayoutSetting, tableSettingsModel);

    // if current logged-in user is the owner of the layout setting, then proceed to save it to local storage
    if (_.isEqual(selectedLayoutSetting.ownerId, this.authService.getCurrentUser()?.id)) {
      // update local storage if it's different with current layout
      // otherwise remove it from local storage
      const backupLayoutSetting:IReportLayoutSetting = this.findLayoutSetting({ gridCode, menuId, storedReportId: selectedLayoutSetting?.storedReportId, ownerId: selectedLayoutSetting?.ownerId }, this.backupLayoutSettings);

      /**
       * Determines whether the layout setting has been modified by comparing it against a backup version.
       *
       * - If `layoutSetting.id` exists, it compares `layoutSetting` with `backupLayoutSetting` using `diffCompareLayoutSettings`.
       * - Otherwise, it compares metadata columns using `diffCompareMetadataColumns`, based on whether `layoutSetting.storedReportId` exists.
       *
       * @returns {boolean} - Returns `true` if the layout setting has changed, otherwise `false`.
       */
      layoutSetting.isChanged = layoutSetting.id ? this.diffCompareLayoutSettings(layoutSetting, backupLayoutSetting) : this.diffCompareMetadataColumns(currentReportMetadata, layoutSetting.storedReportId ? originalStoredReportMetadata : originalReportMetadata);

      // update layout settings
      this.addOrUpdateLayoutSetting(layoutSetting, !!backupLayoutSetting || layoutSetting.isChanged);
    }

    return layoutSetting;
  }

  /**
   * Retrieves the storage layout settings for the report.
   *
   * This method fetches and decodes the layout settings stored locally in the web storage and returns them.
   * If no settings are found, it returns an empty array.
   *
   * @return {IReportLayoutSetting[]} An array of report layout settings.
   */
  private getStorageLayoutSettings():IReportLayoutSetting[] {
    const storageLayoutSettings:string | IReportLayoutSetting[] = this.webStorageService.local.get(STORAGE_REPORT_LAYOUT_SETTINGS);
    // Fallback logic: Previously, a JSON array was stored in localStorage,
    // but now an encoded string containing a JSON object is used.
    // If the content is JSON, then return it as-is.
    if (_.isString(storageLayoutSettings)) {
      return this.decodeLayoutSetting<IReportLayoutSetting[]>(storageLayoutSettings, true) || [];
    } else {
      return storageLayoutSettings || [];
    }
  }

  /**
   * Updates and stores the provided report layout settings after compressing and encoding them.
   * This method also triggers a notification to signal that the storage has changed.
   *
   * @param {IReportLayoutSetting[]} layoutSettings - An array of layout settings for the report to be stored.
   * @return {void} This method does not return any value.
   */
  private setStorageLayoutSettings(layoutSettings:IReportLayoutSetting[]):void {
    const copyLayoutSettings:IReportLayoutSetting = util.deepClone(layoutSettings) || [];
    const encodedLayoutSettings:string = this.encodeLayoutSetting(copyLayoutSettings, true);
    this.webStorageService.local.set(STORAGE_REPORT_LAYOUT_SETTINGS, encodedLayoutSettings);

    // notify report layout settings storage change
    this._onStorageChangeSubject.next(this.getStorageLayoutSettings());
  }

  /**
   * Generates a predicate function to filter layout settings based on the provided criteria.
   *
   * @param {IReportLayoutSetting} layoutSetting - The layout setting to match against.
   * @param {number} [ownerId] - The optional owner ID to override the one in the provided layout setting.
   * @return {ListIterator<IReportLayoutSetting, any>} A function that evaluates whether a given layout setting matches the specified criteria.
   */
  private getLayoutSettingPredicates(layoutSetting:IReportLayoutSetting, ownerId?:number):ListIterator<IReportLayoutSetting, any> {
    return (setting:IReportLayoutSetting):boolean => {
      const ownerIdToCheck:number = ownerId || layoutSetting.ownerId;

      // Base condition with gridCode and ownerId
      const basicPredicate:boolean = setting.gridCode === layoutSetting.gridCode && setting.ownerId === ownerIdToCheck;

      // Check menuId and storedReportId if applicable
      const menuIdPredicate:boolean = layoutSetting.menuId ? setting.menuId === layoutSetting.menuId : !setting.menuId;
      const storedReportIdPredicate:boolean = layoutSetting.storedReportId ? setting.storedReportId === layoutSetting.storedReportId : !setting.storedReportId;

      // Combine all conditions
      return basicPredicate && menuIdPredicate && storedReportIdPredicate;
    };
  }

  /**
   * Retrieves a list of storage layout settings that have been marked as changed.
   *
   * @return {IReportLayoutSetting[]} A list of IReportLayoutSetting objects where the `isChanged` property is true.
   */
  private getChangedStorageLayoutSettings():IReportLayoutSetting[] {
    return _.filter(this.getStorageLayoutSettings(), (layoutSetting:IReportLayoutSetting) => layoutSetting.isChanged);
  }

  /**
   * Retrieves the current time as a Moment object.
   * Make it as getter to make it easy mocking in unit test
   *
   * @return {Moment} The current time.
   */
  public get currentTime():Moment {
    return moment();
  }

  /**
   * Checks whether the current timestamp satisfies the conditions based on the start and end time
   * values retrieved from local storage.
   *
   * The method determines if the time passed since the recorded start time is more than or equal to
   * 'MAX_WAITING_START_SEND_TIME' or if the time passed since the recorded end time is more than or equal to 'MAX_WAITING_END_SEND_TIME'.
   *
   * @return {boolean} Returns true if the current time is at least 'MAX_WAITING_START_SEND_TIME' past the start time
   *                   or 'MAX_WAITING_END_SEND_TIME' past the end time, otherwise false.
   */
  private checkSendTimestamp():boolean {
    const startTime:number = this.webStorageService.local.get(STORAGE_REPORT_LS_START_TIME);
    const endTime:number = this.webStorageService.local.get(STORAGE_REPORT_LS_END_TIME);

    const isMoreThanStartTime:boolean = !startTime || this.currentTime.diff(moment(startTime), 'seconds') >= MAX_WAITING_START_SEND_TIME;
    const isMoreThanEndTime:boolean = !endTime || this.currentTime.diff(moment(endTime), 'seconds') >= MAX_WAITING_END_SEND_TIME;

    return isMoreThanStartTime && isMoreThanEndTime;
  }

  /**
   * Updates the send timestamp in local storage to indicate the start or end of a process.
   * Depending on the value of the isSending parameter, it sets and removes specific keys in local storage.
   *
   * @param {boolean} isSending - A flag indicating whether the process is starting (true) or ending (false).
   */
  private updateSendTimestamp(isSending:boolean):void {
    const currentTimestamp:number = this.currentTime.toDate().getTime();

    if (isSending) {
      this.webStorageService.local.set(STORAGE_REPORT_LS_START_TIME, currentTimestamp);
      this.webStorageService.local.remove(STORAGE_REPORT_LS_END_TIME);
    } else {
      this.webStorageService.local.set(STORAGE_REPORT_LS_END_TIME, currentTimestamp);
      this.webStorageService.local.remove(STORAGE_REPORT_LS_START_TIME);
    }
  }

  /**
   * Activates the auto-save functionality for layout settings.
   * This function sets up a subscription to monitor storage changes. Upon detecting
   * storage changes, it clears any previous interval and starts a new interval task.
   * The interval periodically checks if there are changes in layout settings data,
   * and if so, triggers the process to save these changes to the server.
   * If no changes are detected, it clears the interval to stop unnecessary checks.
   */
  public activateAutoSave = ():void => {
    this.onStorageChange.subscribe((layoutSettings:IReportLayoutSetting[]) => {
      // if parameter is undefined or empty then do nothing
      // this happens because 'BehaviorSubject' will try to
      // send subscription for the first time when it's initialized
      if (_.isEmpty(layoutSettings)) {
        return;
      }

      // clear previous run interval
      this.window.clearInterval(this.intervalId);

      // Start an interval to save updated layout settings to the server
      this.intervalId = this.window.setInterval(() => {
        const isChanged:boolean = this.getChangedStorageLayoutSettings().length > 0;

        // we only trigger save when there are some changes in layout settings data
        if (isChanged) {
          this.saveLayoutSettingsToServer();
        } else {
          // Clear interval when there are no layout settings changes
          this.window.clearInterval(this.intervalId);
        }
      }, LAYOUT_SETTINGS_SEND_INTERVAL);
    });
  };
  
  /**
   * Deletes a layout setting from the database and updates the in-memory layout settings.
   *
   * @param {IReportLayoutSetting} layoutSetting The layout setting object to be deleted, which contains the following properties:
   * gridCode, menuId, and storedReportId that are used to identify the specific layout setting to be removed.
   * @return {Promise<void>} A promise that resolves when the layout setting is successfully removed
   */
  public deleteLayoutSettings(layoutSetting:IReportLayoutSetting):Promise<void> {
    return this.reportDataService.deleteLayoutSetting(layoutSetting.gridCode, layoutSetting.menuId, layoutSetting.storedReportId)
      .then(() => {
        // Use _.reject to exclude the object with a matching key
        const updatedLayoutSettings = _.reject(this.getStorageLayoutSettings(), this.getLayoutSettingPredicates(layoutSetting));

        // Save the updated settings back to storage or assign it to the variable
        this.setStorageLayoutSettings(updatedLayoutSettings);
        
        // Update memory layout settings
        this.layoutSettings = updatedLayoutSettings;
        
        return Promise.resolve();
      })
      .catch(util.noop);
  }
}